// console.log('****** DATA TABLE JS LOADED SUCCESSFULLY ******');
const origin = window.location.origin;
const pathname = window.location.pathname.includes('/pages') ? window.location.pathname : '/pages/resource-center';
const FETCH_URL = `${origin}${pathname}?view=tables-data-template`;
const FETCH_URL_MISC = `${origin}${pathname}?view=misc-tables-data-template`;
const FETCH_URL_BOARDS_AND_PANELS = `${origin}${pathname}?view=extras-data-template`;

const fetchData = async (isMisc = false, isResources = false, isBoardsAndPanels = false) => {
    // console.log('isMisc', isMisc);
    // console.log('isResources', isResources);
    // console.log('isBoardsAndPanels', isBoardsAndPanels);
    try {
        // Validate that at least one data source is requested
        if (!isMisc && !isResources && !isBoardsAndPanels) {
            throw new Error('At least one data source must be specified');
        }
         
        const requests = [];
        
        // Add misc data request if needed
        if (isMisc) {
            requests.push(
                fetch(FETCH_URL_MISC).then(response => {
                    if (!response.ok) {
                        throw new Error(`Misc data fetch failed: HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json().then(data => ({ type: 'misc', data }));
                })
            );
        }

        // Add resources data request if needed
        if (isResources) {
            requests.push(
                fetch(FETCH_URL).then(response => {
                    if (!response.ok) {
                        throw new Error(`Resources data fetch failed: HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json().then(data => ({ type: 'resources', data }));
                })
            );
        }

        if (isBoardsAndPanels) {

            requests.push(
                fetch(FETCH_URL_BOARDS_AND_PANELS).then(response => {
                    if (!response.ok) {
                        throw new Error(`Boards and panels data fetch failed: HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json().then(data => ({ type: 'boards_and_panels', data }));
                })
            );
        }

        // Execute all requests in parallel
        const results = await Promise.all(requests);

        // If only one data source was requested, return it directly
        if (results.length === 1) {
            return results[0].data;
        }

        // If both were requested, combine them
        const combinedData = {};
        results.forEach(result => {
            if (result.type === 'misc') {
                // Prefix misc data keys to avoid conflicts
                Object.keys(result.data).forEach(key => {
                    combinedData[`misc_${key}`] = result.data[key];
                });
            } else if (result.type === 'resources') {
                // Add resources data directly
                Object.assign(combinedData, result.data);
            } else if (result.type === 'boards_and_panels') {
                // Add boards and panels data directly
                Object.keys(result.data).forEach(key => {
                    combinedData[`boards_and_panels_${key}`] = result.data[key];
                });
            }
        });

        // console.log('combinedData', combinedData);
        return combinedData;

    } catch (error) {
        console.error('Failed to load table data:', error);
        this.showError?.('Failed to load table data. Please refresh the page and try again.');
        return null;
    }
}

const loadTables = (allData) => {
    document.querySelectorAll('table.data-table').forEach(async (table) => {
        const datasetName = table.dataset.dataset.toLowerCase();
        // console.log('datasetName', datasetName);
        // Try to find matching key in allData
        const datasetKey = Object.keys(allData).find(key =>
            key.toLowerCase().includes(datasetName)
        );
        // console.log('allData', allData);
        // console.log('datasetKey', datasetKey);
        const data = allData[datasetKey].data;
        
        // Use headers from the data structure if available, otherwise extract from data
        const headers = allData[datasetKey].table_headers || 
            (data.length > 0 ? Object.keys(data[0]).filter(key => key !== 'url' && !key.startsWith('_') && key !== 'image') : []);

        new DataTable(table, datasetName, data, headers);
    });
}

document.addEventListener('DOMContentLoaded', async () => {
    const allTables = Array.from(document.querySelectorAll('table.data-table'));
    const isMisc = allTables.some(table => table.dataset.tableType === 'misc');
    const isResources = allTables.some(table => table.dataset.tableType === 'resources');
    const isBoardsAndPanels = allTables.some(table => table.dataset.tableType === 'boards_and_panels');

    //console.log('allTables', allTables);

    if(!isMisc && !isResources && !isBoardsAndPanels) {
        return;
    }

    const allData = await fetchData(isMisc, isResources, isBoardsAndPanels);
    // console.log('allData', allData);
    if(!allData) {
        return;
    }

    // //console.log('allData', allData);
    loadTables(allData);
});

class DataTable {
    constructor(tableElement, dataset, data, headers = []) {
        this.showError = (message) => {
            console.error(message);
        }
        
        this.tableElement = tableElement;
        this.dataset = dataset;
        this.originalData = data;
        this.filteredData = data;
        this.tempFilteredData = data;
        this.headers = headers; // Use provided headers
        this.searchInput = tableElement.closest('.data-table__container')?.querySelector('.table-search-input');
        this.sortBtns = tableElement.closest('.data-table__container').querySelectorAll('.sort-btn');
        this.currentSortColumn = null;
        this.currentSortDirection = 'asc';
        this.openLinksInNewTab = tableElement.closest('.data-table__container').getAttribute('data-open-links-in-new-tab') === 'true';

        // Icons
        this.sortIconSrc = tableElement.closest('.data-table__container').getAttribute('data-sort-icon');
        this.filterIconSrc = tableElement.closest('.data-table__container').getAttribute('data-filter-icon');
        this.chevronDownIconSrc = tableElement.closest('.data-table__container').getAttribute('data-chevron-down-icon');
        
        // Check if toolbar exists
        this.hasToolbar = !!tableElement.closest('.data-table__container')?.querySelector('.data-table__toolbar');
        
        // Pagination settings
        this.rowsPerPage = parseInt(this.tableElement.closest('.data-table__container').getAttribute('data-rows-per-page'));
        this.currentPage = 1; // Start from page 1 for user-friendly display
        this.searchDebounceTimer = null;
        this.searchDebounceDelay = 300;

        // Auto sort settings
        this.autoSortTable = tableElement.closest('.data-table__container').getAttribute('data-auto-sort-table') === 'true';
        this.autoSortColumn = tableElement.closest('.data-table__container').getAttribute('data-auto-sort-column');
        const rawDirection = tableElement.closest('.data-table__container').getAttribute('data-auto-sort-direction');
        // Normalize direction: support both 'asc'/'desc' and 'ascending'/'descending'
        this.autoSortDirection = this.normalizeDirection(rawDirection);
        
        // Column toggle settings
        this.hiddenColumns = new Set();
        this.columnKeys = [...this.headers]; // Use headers as column keys
        
        // Filter settings
        this.activeFilters = {}; // Track active filters for each column
        this.enableValueFilters = tableElement.closest('.data-table__container').getAttribute('data-enable-value-filters') === 'true';
        
        // Cache DOM elements
        this.tbody = this.tableElement.querySelector('tbody');
        this.thead = this.tableElement.querySelector('thead');
        this.toolbarButtons = this.hasToolbar ? this.tableElement.closest('.data-table__container').querySelector('.data-table__toolbar-buttons') : null;
        this.dropdownToggle = this.hasToolbar ? this.tableElement.closest('.data-table__container').querySelector('.dropdown-toggle') : null;
        this.dropdownMenu = this.hasToolbar ? this.tableElement.closest('.data-table__container').querySelector('.dropdown-menu') : null;
        this.container = this.tableElement.closest('.data-table__container');
        
        // Find existing pagination container
        this.paginationContainer = this.tableElement.closest('.data-table__container').querySelector('.data-table__pagination');

        // Logging the data
        // console.log('Data', data); // Debug: Log data for inspection
        
        // Pre-process data for better performance
        this.processedData = this.preprocessData(data);
        
        //console.log('DataTable initialized with', data.length, 'items');
        this.init();
        this.initializeAccessibility();
    }

    // Check if we're on mobile (dynamic check)
    isMobile() {
        return window.innerWidth <= 768;
    }

    preprocessData(data) {
        // Create searchable text for each item to avoid repeated processing
        return data.map(item => (
            {
                ...item,
                _searchText: Object.values(item)
                    .filter(val => typeof val === 'string')
                    .join(' ')
                    .toLowerCase() + 
                    (item.courses ? ' ' + item.courses.map(course => course.title).join(' ').toLowerCase() : '' ) // For items that have courses
            }
        ));
    }

    showLoading(message = 'Loading data...') {
        if (this.container) {
            this.container.classList.add('loading');
            this.container.classList.remove('loaded');
            
            const loadingText = this.container.querySelector('.loading-text');
            if (loadingText) {
                loadingText.textContent = message;
            }
        }
    }

    hideLoading() {
        if (this.container) {
            this.container.classList.remove('loading');
            
            // Add loaded class for fade-in animation
            setTimeout(() => {
                this.container.classList.add('loaded');
            }, 100);
        }
    }

    async init() {
        this.showLoading('Initializing table...');
        
        // Simulate some processing time for better UX
        await new Promise(resolve => setTimeout(resolve, 300));
        
        this.createHeaders();
        
        // Only create toolbar features if toolbar exists
        if (this.hasToolbar) {
            this.createColumnToggleButtons();
            this.createColumnDropdown();
        }
        
        this.attachEvents();
        
        // Create mobile filter panel if on mobile
        if (this.isMobile()) {
            this.createMobileFilterPanel();
        }
        
        this.showLoading('Rendering data...');
        await new Promise(resolve => setTimeout(resolve, 200));
        
        this.render();
        
        // Hide loading after everything is ready
        setTimeout(() => {
            if(this.autoSortTable) {
                // Map the display name to the actual field name
                const sortFieldName = this.getFieldNameFromHeader(this.autoSortColumn);
                
                if (sortFieldName) {
                    this.sortByColumn(sortFieldName, this.autoSortDirection, null);
                }
            }
            
            this.hideLoading();
            
            // Set dynamic min-height after initial render
            this.setDynamicMinHeight();
        }, 100);
    }

    setDynamicMinHeight() {
        if (!this.container) return;
        
        // Get the current height of the container
        const currentHeight = this.container.offsetHeight;
        
        this.container.style.minHeight = `${currentHeight}px`;
    }

    createHeaders() {
        if (this.headers && this.headers.length > 0) {
            // Use actual data field names as column keys, not header display names
            if (this.originalData && this.originalData.length > 0) {
                this.columnKeys = Object.keys(this.originalData[0])
                    .filter(key => key !== 'url' && !key.startsWith('_') && key !== 'image');
                //console.log('Using data field names as column keys:', this.columnKeys);
            } else {
                // Fallback to provided headers if no data
                this.columnKeys = [...this.headers];
                //console.log('Using provided headers as fallback:', this.columnKeys);
            }
        } else {
            // Fallback to extracting from data if no headers provided
            this.columnKeys = Object.keys(this.originalData[0] || {})
                .filter(key => key !== 'url' && !key.startsWith('_') && key !== 'image');
            //console.log('Extracted headers from data:', this.columnKeys);
        }
        
        this.updateHeaderDisplay();
    }

    createColumnDropdown() {
        if (!this.dropdownMenu || !this.columnKeys.length) return;
        
        const dropdownItems = this.columnKeys.map((key, index) => {
            if(index === 0) return; // Skip the first column (Filter)
            const isVisible = !this.hiddenColumns.has(key);
            const id = `column-${key}-${this.dataset}`;
            
            // Use the provided header names from this.headers
            let displayName;
            if (this.headers && this.headers[index]) {
                displayName = this.headers[index];
            } else {
                // Fallback to formatted field name if header not available
                displayName = this.formatHeaderName(key);
            }
            
            return `
                <div class="dropdown-item">
                    <input type="checkbox" id="${id}" ${isVisible ? 'checked' : ''} data-column="${key}">
                    <label for="${id}">${displayName}</label>
                </div>
            `;
        }).join('');
        
        this.dropdownMenu.innerHTML = dropdownItems;
        
        // Attach change events to checkboxes
        this.dropdownMenu.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
            checkbox.addEventListener('change', (e) => {
                const column = e.target.getAttribute('data-column');
                const isChecked = e.target.checked;
                
                if (isChecked) {
                    this.showColumn(column);
                } else {
                    this.hideColumn(column);
                }
            });
        });
    }

    createColumnToggleButtons() {
        if (!this.toolbarButtons || !this.columnKeys.length) return;
        
        // Only show buttons for visible columns
        const visibleColumns = this.columnKeys.filter(key => !this.hiddenColumns.has(key));
        
        const buttons = visibleColumns.map((key, index) => {
            // Find the original index to get the correct header
            const originalIndex = this.columnKeys.indexOf(key);

            // Use the provided header names from this.headers
            let displayName;
            if (this.headers && this.headers[originalIndex]) {
                displayName = this.headers[originalIndex];
            } else {
                // Fallback to formatted field name if header not available
                displayName = this.formatHeaderName(key);
            }
            
            return `
                <button class="column-toggle-btn" data-column="${key}">
                    ${displayName}
                    <span class="toggle-icon">✕</span>
                </button>
            `;
        }).join('');
        
        this.toolbarButtons.innerHTML = buttons;
        
        // Attach click events to hide buttons
        this.toolbarButtons.querySelectorAll('.column-toggle-btn').forEach((btn, index) => {
            // Check if this button corresponds to the first column
            const key = btn.getAttribute('data-column');
            const originalIndex = this.columnKeys.indexOf(key);
            
            if(originalIndex === 0) return; // Skip the first button (Filter)
            btn.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column');
                this.hideColumn(column);
            });
        });
    }

    hideColumn(columnKey) {
        this.hiddenColumns.add(columnKey);
        
        // Only update toolbar elements if toolbar exists
        if (this.hasToolbar) {
            // Update dropdown checkbox
            const checkbox = this.dropdownMenu?.querySelector(`[data-column="${columnKey}"]`);
            if (checkbox) {
                checkbox.checked = false;
            }
            
            // Remove the tag button
            const button = this.toolbarButtons?.querySelector(`[data-column="${columnKey}"]`);
            if (button) {
                button.remove();
            }
        }
        
        // Re-render table
        this.updateHeaderDisplay();
        this.renderTable();
        
        //console.log(`Column "${columnKey}" hidden`);
    }

    showColumn(columnKey) {
        this.hiddenColumns.delete(columnKey);
        
        // Only update toolbar elements if toolbar exists
        if (this.hasToolbar) {
            // Update dropdown checkbox
            const checkbox = this.dropdownMenu?.querySelector(`[data-column="${columnKey}"]`);
            if (checkbox) {
                checkbox.checked = true;
            }
            
            // Recreate all tag buttons to maintain order
            this.createColumnToggleButtons();
        }
        
        // Re-render table
        this.updateHeaderDisplay();
        this.renderTable();
        
        //console.log(`Column "${columnKey}" shown`);
    }

    formatHeaderName(key) {
        return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    }

    // Normalize sort direction to 'asc' or 'desc'
    normalizeDirection(direction) {
        if (!direction) return 'asc';
        const normalized = direction.toLowerCase().trim();
        if (normalized === 'ascending' || normalized === 'asc') return 'asc';
        if (normalized === 'descending' || normalized === 'desc') return 'desc';
        return 'asc'; // default fallback
    }

    // Get field name from header display name
    getFieldNameFromHeader(headerDisplayName) {
        // First, try exact match with column keys
        if (this.columnKeys.includes(headerDisplayName)) {
            return headerDisplayName;
        }
        
        // Try to find by matching with header names
        const index = this.headers.findIndex(header => 
            header.toLowerCase() === headerDisplayName.toLowerCase()
        );
        
        if (index !== -1 && this.columnKeys[index]) {
            return this.columnKeys[index];
        }
        
        // Fallback: try to find a column key that matches when formatted
        const matchingKey = this.columnKeys.find(key => 
            this.formatHeaderName(key).toLowerCase() === headerDisplayName.toLowerCase()
        );
        
        return matchingKey || null;
    }

    attachEvents() {
        // Only attach toolbar events if toolbar exists
        if (this.hasToolbar) {
            if (this.searchInput) {
                this.searchInput.addEventListener('input', (e) => {
                    // Debounce search for better performance
                    clearTimeout(this.searchDebounceTimer);
                    this.searchDebounceTimer = setTimeout(() => {
                        this.handleSearch(e.target.value);
                    }, this.searchDebounceDelay);
                });
            }

            // Dropdown toggle functionality
            if (this.dropdownToggle && this.dropdownMenu) {
                this.dropdownToggle.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.dropdownMenu.classList.toggle('show');
                });

                // Close dropdown when clicking outside
                document.addEventListener('click', (e) => {
                    if (!this.dropdownToggle.contains(e.target) && !this.dropdownMenu.contains(e.target)) {
                        this.dropdownMenu.classList.remove('show');
                    }
                });

                // Prevent dropdown from closing when clicking inside
                this.dropdownMenu.addEventListener('click', (e) => {
                    e.stopPropagation();
                });
            }

            // Mobile filters toggle
            const mobileFiltersToggle = this.container.querySelector('.mobile-filters-toggle');
            if (mobileFiltersToggle) {
                mobileFiltersToggle.addEventListener('click', () => {
                    this.toggleMobileFilterPanel();
                });
            }
        }
    }

    createMobileFilterPanel() {
        const panel = document.createElement('div');
        panel.className = 'mobile-filter-panel';
        panel.setAttribute('id', `mobile-filter-panel-${this.dataset}`);
        
        const filterSections = this.columnKeys.map((key, index) => {
            const displayName = this.headers?.[index] || this.formatHeaderName(key);
            const uniqueValues = this.getUniqueColumnValues(key);
            
            // Skip if no values
            if (uniqueValues.length === 0) return '';
            
            return `
                <div class="mobile-filter-section">
                    <button class="mobile-filter-accordion" data-column="${key}">
                        <span>${displayName}</span>
                        <span class="accordion-icon">▼</span>
                    </button>
                    <div class="mobile-filter-options" id="mobile-filter-${this.dataset}-${key}">
                        ${uniqueValues.map(value => `
                            <label class="mobile-filter-item">
                                <input type="checkbox" value="${value}" data-column="${key}" class="mobile-filter-checkbox" />
                                <span>${value}</span>
                            </label>
                        `).join('')}
                    </div>
                </div>
            `;
        }).join('');
        
        panel.innerHTML = `
            <div class="mobile-filter-header">
                <h3>Filter Table</h3>
                <button class="close-filters-btn" aria-label="Close filters">✕</button>
            </div>
            <div class="mobile-filter-content">
                ${filterSections}
            </div>
            <div class="mobile-filter-actions">
                <button class="apply-all-filters-btn">Apply Filters</button>
                <button class="clear-all-filters-btn">Clear All</button>
            </div>
        `;
        
        this.container.appendChild(panel);
        this.attachMobileFilterEvents(panel);
    }

    attachMobileFilterEvents(panel) {
        // Close button
        const closeBtn = panel.querySelector('.close-filters-btn');
        closeBtn.addEventListener('click', () => {
            this.closeMobileFilterPanel();
        });

        // Accordion toggles
        const accordions = panel.querySelectorAll('.mobile-filter-accordion');
        accordions.forEach(accordion => {
            accordion.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column');
                const options = panel.querySelector(`#mobile-filter-${this.dataset}-${column}`);
                const icon = e.currentTarget.querySelector('.accordion-icon');
                
                // Toggle this accordion
                const isOpen = options.classList.contains('open');
                if (isOpen) {
                    options.classList.remove('open');
                    icon.style.transform = 'rotate(0deg)';
                } else {
                    options.classList.add('open');
                    icon.style.transform = 'rotate(180deg)';
                }
            });
        });

        // Apply filters button
        const applyBtn = panel.querySelector('.apply-all-filters-btn');
        applyBtn.addEventListener('click', () => {
            this.applyMobileFilters();
        });

        // Clear all button
        const clearBtn = panel.querySelector('.clear-all-filters-btn');
        clearBtn.addEventListener('click', () => {
            this.clearAllMobileFilters();
        });

        // Close panel when clicking backdrop
        panel.addEventListener('click', (e) => {
            // Close if clicking the backdrop (::before pseudo-element area)
            const rect = panel.getBoundingClientRect();
            const clickedOutsidePanel = e.clientY < rect.top;
            
            if (clickedOutsidePanel) {
                this.closeMobileFilterPanel();
            }
        });
    }

    toggleMobileFilterPanel() {
        const panel = document.getElementById(`mobile-filter-panel-${this.dataset}`);
        if (panel) {
            panel.classList.toggle('open');
            
            // Update checkboxes to reflect current active filters
            if (panel.classList.contains('open')) {
                this.syncMobileFilterCheckboxes();
            }
        }
    }

    closeMobileFilterPanel() {
        const panel = document.getElementById(`mobile-filter-panel-${this.dataset}`);
        if (panel) {
            panel.classList.remove('open');
        }
    }

    syncMobileFilterCheckboxes() {
        // Update mobile checkboxes to match current activeFilters
        Object.keys(this.activeFilters).forEach(columnKey => {
            const allowedValues = this.activeFilters[columnKey];
            const checkboxes = document.querySelectorAll(`#mobile-filter-${this.dataset}-${columnKey} .mobile-filter-checkbox`);
            
            checkboxes.forEach(checkbox => {
                checkbox.checked = allowedValues.includes(checkbox.value);
            });
        });
    }

    applyMobileFilters() {
        // Collect all checked values from mobile panel
        const panel = document.getElementById(`mobile-filter-panel-${this.dataset}`);
        const allCheckboxes = panel.querySelectorAll('.mobile-filter-checkbox');
        
        // Clear existing filters
        this.activeFilters = {};
        
        // Group checked values by column
        allCheckboxes.forEach(checkbox => {
            if (checkbox.checked) {
                const column = checkbox.getAttribute('data-column');
                if (!this.activeFilters[column]) {
                    this.activeFilters[column] = [];
                }
                this.activeFilters[column].push(checkbox.value);
            }
        });
        
        // Apply all filters
        this.applyAllFilters();
        
        // Close the panel
        this.closeMobileFilterPanel();
    }

    clearAllMobileFilters() {
        const panel = document.getElementById(`mobile-filter-panel-${this.dataset}`);
        const allCheckboxes = panel.querySelectorAll('.mobile-filter-checkbox');
        
        // Uncheck all
        allCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        });
        
        // Clear filters
        this.activeFilters = {};
        this.applyAllFilters();
        
        // Close the panel
        this.closeMobileFilterPanel();
    }

    handleSearch(query) {
        // Add searching class for animation
        this.tbody.classList.add('searching');
        this.tbody.classList.remove('search-complete');
        
        // Add updating class to pagination
        if (this.paginationContainer) {
            this.paginationContainer.classList.add('updating');
        }
        
        const searchTerm = query.toLowerCase().trim();
        
        if (!searchTerm) {
            this.filteredData = this.processedData;
        } else {
            // Use pre-processed search text for faster filtering
            this.filteredData = this.processedData.filter(item => 
                item._searchText.includes(searchTerm)
            );
        }
        
        this.currentPage = 1; // Reset to first page
        
        // Add delay for smooth animation
        setTimeout(() => {
            this.render();
            
            // Complete search animation
            setTimeout(() => {
                this.tbody.classList.remove('searching');
                this.tbody.classList.add('search-complete');
                
                if (this.paginationContainer) {
                    this.paginationContainer.classList.remove('updating');
                }
                
                //console.log(`Search: "${query}" - ${this.filteredData.length} results`);
            }, 100);
        }, 150);
    }

    getTotalPages() {
        return Math.ceil(this.filteredData.length / this.rowsPerPage);
    }

    getCurrentPageData() {
        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        const endIndex = startIndex + this.rowsPerPage;
        return this.filteredData.slice(startIndex, endIndex);
    }

    render() {
        this.renderTable();
        this.renderPagination();
    }

    renderTable() {
        this.updateHeaderDisplay();
        const pageData = this.getCurrentPageData();
        
        if (pageData.length === 0) {
            this.tbody.innerHTML = '<tr><td colspan="100%">No data found</td></tr>';
            return;
        }

        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        
        pageData.forEach(item => {
            const row = this.createRow(item);
            fragment.appendChild(row);
        });
        
        // Clear existing content and add new rows
        this.tbody.innerHTML = '';
        this.tbody.appendChild(fragment);
        
        // Reset animations for new rows
        const rows = this.tbody.querySelectorAll('tr');
        rows.forEach((row, index) => {
            // Reset animation by removing and re-adding
            row.style.animation = 'none';
            row.offsetHeight; // Trigger reflow
            row.style.animation = `fadeInRow 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards`;
            row.style.animationDelay = `${(index + 1) * 0.05}s`;
        });
        
        const startIndex = (this.currentPage - 1) * this.rowsPerPage + 1;
        const endIndex = Math.min(this.currentPage * this.rowsPerPage, this.filteredData.length);
        //console.log(`Rendered page ${this.currentPage}, rows ${startIndex}-${endIndex}`);
    }

    renderPagination() {
        const totalPages = this.getTotalPages();
        const infoElement = this.paginationContainer.querySelector('.pagination-info');
        const controlsElement = this.paginationContainer.querySelector('.pagination-controls');
        
        // Update info
        const startIndex = (this.currentPage - 1) * this.rowsPerPage + 1;
        const endIndex = Math.min(this.currentPage * this.rowsPerPage, this.filteredData.length);
        infoElement.textContent = `Showing ${startIndex}-${endIndex} of ${this.filteredData.length} entries`;
        
        if (totalPages <= 1) {
            controlsElement.innerHTML = '';
            return;
        }

        let paginationHTML = '';
        
        // Previous button
        paginationHTML += `<button class="pagination-btn" ${this.currentPage === 1 ? 'disabled' : ''} data-page="${this.currentPage - 1}">‹ Prev</button>`;
        
        // Page numbers with smart display
        const startPage = Math.max(1, this.currentPage - 2);
        const endPage = Math.min(totalPages, this.currentPage + 2);

        // First page + ellipsis if needed
        if (startPage > 1) {
            paginationHTML += `<button class="pagination-btn" data-page="1">1</button>`;
            if (startPage > 2) {
                paginationHTML += `<span class="pagination-dots">...</span>`;
            }
        }

        // Page numbers around current page
        for (let i = startPage; i <= endPage; i++) {
            const isActive = i === this.currentPage;
            paginationHTML += `<button class="pagination-btn ${isActive ? 'active' : ''}" data-page="${i}">${i}</button>`;
        }

        // Last page + ellipsis if needed
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                paginationHTML += `<span class="pagination-dots">...</span>`;
            }
            paginationHTML += `<button class="pagination-btn" data-page="${totalPages}">${totalPages}</button>`;
        }

        // Next button
        paginationHTML += `<button class="pagination-btn" ${this.currentPage === totalPages ? 'disabled' : ''} data-page="${this.currentPage + 1}">Next ›</button>`;

        controlsElement.innerHTML = paginationHTML;
        
        // Attach click events to pagination buttons
        controlsElement.querySelectorAll('.pagination-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const page = parseInt(e.target.getAttribute('data-page'));
                if (page && page !== this.currentPage && !e.target.disabled) {
                    this.goToPage(page);
                }
            });
        });
    }

    goToPage(page) {
        const totalPages = this.getTotalPages();
        if (page < 1 || page > totalPages) return;
        
        // Add animation classes
        this.tbody.classList.add('searching');
        if (this.paginationContainer) {
            this.paginationContainer.classList.add('updating');
        }
        
        this.currentPage = page;
        
        // Smooth transition
        setTimeout(() => {
            this.render();
            
            // Complete animation
            setTimeout(() => {
                this.tbody.classList.remove('searching');
                this.tbody.classList.add('search-complete');
                
                if (this.paginationContainer) {
                    this.paginationContainer.classList.remove('updating');
                }
            }, 100);
        }, 100);
    }

    createRow(item) {
        const row = document.createElement('tr');
        row.setAttribute('role', 'row');
        
        const { isValid, hasUrl, hasImage, hasCourses, hasDegrees, hasApprovalDates } = this.validateDataItem(item);
        
        if (!isValid) {
            console.error('Invalid data item:', item);
            return row;
        }
        
        // Add mobile card classes
        if (this.isMobile()) {
            row.classList.add('mobile-row', 'card-collapsed');
        }
        
        // Only process visible columns
        const visibleColumns = this.columnKeys.filter(key => !this.hiddenColumns.has(key));
        
        visibleColumns.forEach((key, index) => {
            if (key === 'url' || key.startsWith('_') || key === 'image') return;
            
            const cell = document.createElement('td');
            cell.setAttribute('data-column', key);
            cell.setAttribute('role', 'cell');
            
            // Add data label for mobile cards (except first cell)
            if (this.isMobile() && index > 0) {
                const originalIndex = this.columnKeys.indexOf(key);
                let displayName;
                if (this.headers && this.headers[originalIndex]) {
                    displayName = this.headers[originalIndex];
                } else {
                    displayName = this.formatHeaderName(key);
                }
                cell.classList.add('mobile-cell');
                
                // Create flexbox container for label and value
                const cellContainer = document.createElement('div');
                if(hasCourses && key === 'courses') {
                    cellContainer.classList.add('mobile-cell-container-courses');
                    cellContainer.classList.add(`has-courses`);
                } else if (hasDegrees && key === 'degrees') {
                    cellContainer.classList.add('mobile-cell-container-degrees');
                    cellContainer.classList.add(`has-degrees`);
                } else if (hasApprovalDates && key === 'approval_dates') {
                    cellContainer.classList.add('mobile-cell-container-approval-dates');
                    cellContainer.classList.add(`has-approval-dates`);
                } else {
                    cellContainer.classList.add('mobile-cell-container');
                }
                
                // Create label element
                const labelElement = document.createElement('span');
                labelElement.classList.add('mobile-cell-label');
                labelElement.textContent = displayName + ':';
                
                // Create value container
                const valueElement = document.createElement('span');
                valueElement.classList.add('mobile-cell-value');
                
                cellContainer.appendChild(labelElement);
                cellContainer.appendChild(valueElement);
                cell.appendChild(cellContainer);
            }
            

            if (key === 'courses' && hasCourses) {
                // Handle courses array efficiently
                const coursesList = document.createElement('ul');
                item.courses.forEach(course => {
                    const listItem = document.createElement('li');
                    listItem.textContent = course.title || course;
                    coursesList.appendChild(listItem);
                });
                
                // Add to appropriate container
                if (this.isMobile() && index > 0) {
                    const valueContainer = cell.querySelector('.mobile-cell-value');
                    valueContainer.appendChild(coursesList);
                } else {
                    cell.appendChild(coursesList);
                }
            } else if (key === 'degrees' && hasDegrees) {
                // Handle degrees array with links
                const degreesList = this.createLinkedList(item.degrees, 'degree', 'degree_url');
                
                // Add to appropriate container
                if (this.isMobile() && index > 0) {
                    const valueContainer = cell.querySelector('.mobile-cell-value');
                    valueContainer.appendChild(degreesList);
                } else {
                    cell.appendChild(degreesList);
                }
            } else if (key === 'approval_dates' && hasApprovalDates) {
                // Handle approval dates array with links
                const approvalDatesList = this.createLinkedList(item.approval_dates, 'approval_date', 'approval_date_url');
                
                // Add to appropriate container
                if (this.isMobile() && index > 0) {
                    const valueContainer = cell.querySelector('.mobile-cell-value');
                    valueContainer.appendChild(approvalDatesList);
                } else {
                    cell.appendChild(approvalDatesList);
                }
            } else {
                const value = item[key];
                const isFirstVisibleCell = index === 0;
                
                // Handle first visible cell with potential image and/or link
                if (isFirstVisibleCell) {
                    if (this.isMobile()) {
                        // Mobile: Create structured first cell
                        cell.classList.add('mobile-cell', 'first-cell');
                        
                        // Create content container
                        const contentDiv = document.createElement('div');
                        contentDiv.classList.add('cell-content');
                        
                        // Add image if exists
                        if (hasImage) {
                            const img = this.createImage(item.image, item.name || item.title || 'Image');
                            contentDiv.appendChild(img);
                            cell.classList.add('has-image');
                        }
                        
                        // Add link or text content
                        if (hasUrl) {
                            const link = this.createLink(item.url, value);
                            contentDiv.appendChild(link);
                        } else {
                            const textNode = document.createTextNode(value);
                            contentDiv.appendChild(textNode);
                        }
                        
                        // Add expand button
                        const expandBtn = document.createElement('button');
                        expandBtn.classList.add('expand-btn');
                        expandBtn.innerHTML = `<img src="https:${this.chevronDownIconSrc}" alt="Expand" role="presentation" />`;
                        expandBtn.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            this.toggleCard(row);
                        });
                        
                        cell.appendChild(contentDiv);
                        cell.appendChild(expandBtn);
                    } else {
                        // Desktop: Normal cell structure
                        if (hasImage) {
                            const img = this.createImage(item.image, item.name || item.title || 'Image');
                            cell.appendChild(img);
                            cell.classList.add('has-image');
                        }
                        
                        if (hasUrl) {
                            const link = this.createLink(item.url, value);
                            cell.appendChild(link);
                        } else {
                            const textNode = document.createTextNode(value);
                            cell.appendChild(textNode);
                        }
                    }
                } else {
                    // For non-first cells
                    if (this.isMobile()) {
                        // Mobile: Add content to value container
                        if(item[`_${key}_url`]) {
                            const link = this.createLink(item[`_${key}_url`], value);
                            const valueContainer = cell.querySelector('.mobile-cell-value');
                            valueContainer.appendChild(link);
                        } else {
                            const valueContainer = cell.querySelector('.mobile-cell-value');
                            valueContainer.textContent = value;
                        }
                    } else {
                        if(item[`_${key}_url`]) {
                            const link = this.createLink(item[`_${key}_url`], value);
                            cell.appendChild(link);
                        } else {
                            // Desktop: Add content directly to cell
                            cell.textContent = value;
                        }
                    }
                }
            }
            
            row.appendChild(cell);
        });
        
        return row;
    }

    toggleCard(row) {
        const isCollapsed = row.classList.contains('card-collapsed');
        
        if (isCollapsed) {
            // Expand the card
            row.classList.remove('card-collapsed');
            row.classList.add('card-expanded');
        } else {
            // Collapse the card
            row.classList.remove('card-expanded');
            row.classList.add('card-collapsed');
        }
        
        // Add accessibility
        const expandBtn = row.querySelector('.expand-btn');
        if (expandBtn) {
            expandBtn.setAttribute('aria-expanded', !isCollapsed);
        }
    }

    // Public method to refresh data
    refreshData(newData) {
        this.originalData = newData;
        this.processedData = this.preprocessData(newData);
        this.filteredData = this.processedData;
        this.currentPage = 1;
        this.createHeaders();
        this.render();
        //console.log('Data refreshed with', newData.length, 'items');
    }

    // Public method to get current stats
    getStats() {
        return {
            total: this.originalData.length,
            filtered: this.filteredData.length,
            currentPage: this.currentPage,
            totalPages: this.getTotalPages(),
            rowsPerPage: this.rowsPerPage
        };
    }

    getUniqueColumnValues(key) {
        // console.log(`\n🔎 Getting unique values for column: "${key}"`); // Debug: Log column being processed
        // console.log(`  Total rows in filteredData: ${this.filteredData.length}`); // Debug: Log total rows
        
        // Let's check first few items
        // console.log(`  Sample data for "${key}":`, this.filteredData.slice(0, 3).map(item => ({ // Debug: Log sample data
        //     value: item[key],
        //     type: typeof item[key],
        //     isArray: Array.isArray(item[key])
        // })));
        
        // Check for rows with "Classroom"
        // const classroomRows = this.filteredData.filter(item => { // Debug: Check for specific values
        //     const value = item[key];
        //     if (typeof value === 'string') {
        //         return value.trim().toLowerCase().includes('classroom');
        //     }
        //     return false;
        // });
        // console.log(`  Rows containing "Classroom": ${classroomRows.length}`,  // Debug: Log classroom rows
        //             classroomRows.slice(0, 2).map(item => item[key]));
        
        const valuesSet = new Set();
        
        this.filteredData.forEach(item => {
            const value = item[key];
            
            // Skip null/undefined
            if (value === null || value === undefined || value === '') {
                return;
            }
            
            // Handle arrays (like courses, degrees, approval_dates)
            if (Array.isArray(value)) {
                // console.log(`  Array value found:`, value); // Debug: Log array values
                value.forEach(item => {
                    if (typeof item === 'object') {
                        // Check for different property names
                        const textValue = item.title || item.degree || item.approval_date || item.name;
                        if (textValue) {
                            // console.log(`    Adding from object: "${textValue}"`); // Debug: Log object value added
                            valuesSet.add(textValue);
                        } else {
                            // console.log(`    ⚠️ Object found but no recognizable property:`, item); // Debug: Log unrecognized object
                        }
                    } else if (typeof item === 'string') {
                        // console.log(`    Adding string: "${item}"`); // Debug: Log string value added
                        valuesSet.add(item);
                    }
                });
                return;
            }
            
            // Handle comma-separated strings (like "AL, AZ, AR")
            if (typeof value === 'string' && value.includes(',')) {
                // console.log(`  Comma-separated value found: "${value}"`); // Debug: Log comma-separated values
                value.split(',').forEach(v => {
                    const trimmed = v.trim();
                    if (trimmed) {
                        // console.log(`    Adding: "${trimmed}"`); // Debug: Log trimmed value added
                        valuesSet.add(trimmed);
                    }
                });
                return;
            }
            
            // Handle single values
            // console.log(`  Single value: "${value}"`); // Debug: Log single value
            valuesSet.add(String(value));
        });
        
        const sortedValues = Array.from(valuesSet).sort();
        // console.log(`  ✅ Total unique values found: ${sortedValues.length}`, sortedValues.slice(0, 10)); // Debug: Log final unique values
        return sortedValues;
    }

    toggleFilterOptions(columnKey) {
        const filterOptions = document.getElementById(`filter-options-${this.dataset}-${columnKey}`);
        const allFilterOptions = document.querySelectorAll(`.filter-options:not(#filter-options-${this.dataset}-${columnKey})`); // all filter options except the current one
        
        // Close all other filter dropdowns
        allFilterOptions.forEach(dropdown => {
            if (dropdown.id !== `filter-options-${columnKey}`) {
                dropdown.classList.add('hidden');
            }
        });
        
        // Toggle current dropdown
        if (filterOptions) {
            filterOptions.classList.toggle('hidden');
        }
    }

    applyFilter(columnKey) {
        const filterOptions = document.getElementById(`filter-options-${this.dataset}-${columnKey}`);
        const filterCheckboxes = filterOptions.querySelectorAll('.filter-checkbox');
        const filterValues = Array.from(filterCheckboxes).filter(checkbox => checkbox.checked).map(checkbox => checkbox.value);
        // console.log('filterValues', filterValues); // Debug: Log selected filter values

        // Update activeFilters
        if (filterValues.length > 0) {
            this.activeFilters[columnKey] = filterValues;
        } else {
            delete this.activeFilters[columnKey];
        }

        // Use applyAllFilters which handles multi-value cells correctly
        this.applyAllFilters();
    }

    clearFilter(columnKey) {
        const filterOptions = document.getElementById(`filter-options-${this.dataset}-${columnKey}`);
        const filterCheckboxes = filterOptions.querySelectorAll('.filter-checkbox');
        filterCheckboxes.forEach(checkbox => {
            checkbox.checked = false;
        })
        this.applyFilter(columnKey);
    }

    applyAllFilters() {
        // console.log('🔍 Starting applyAllFilters'); // Debug: Log filter start
        // console.log('Active filters:', this.activeFilters); // Debug: Log active filters
        
        let filteredData = this.processedData;
        // console.log('Initial data count:', filteredData.length); // Debug: Log initial count
        
        // Apply search filter first if there's a search term
        if (this.searchInput && this.searchInput.value.trim()) {
            const searchTerm = this.searchInput.value.toLowerCase().trim();
            filteredData = filteredData.filter(item => 
                item._searchText.includes(searchTerm)
            );
            // console.log('After search filter:', filteredData.length); // Debug: Log count after search
        }
        
        // Apply each active column filter
        Object.keys(this.activeFilters).forEach(columnKey => {
            const allowedValues = this.activeFilters[columnKey];
            // console.log(`\n📊 Filtering column: "${columnKey}"`); // Debug: Log column being filtered
            // console.log('Allowed values:', allowedValues); // Debug: Log allowed values
            
            const beforeCount = filteredData.length;
            
            filteredData = filteredData.filter(item => {
                const cellValue = item[columnKey];
                
                // Handle arrays (like courses, degrees, approval_dates)
                if (Array.isArray(cellValue)) {
                    const matches = cellValue.some(item => {
                        // Extract the text value from the object
                        const textValue = typeof item === 'object' 
                            ? (item.title || item.degree || item.approval_date || item.name)
                            : item;
                        const match = allowedValues.includes(String(textValue));
                        // if (match) { // Debug: Log array match
                        //     console.log(`  ✓ Array match: "${textValue}"`);
                        // }
                        return match;
                    });
                    // if (!matches) { // Debug: Log no array match
                    //     console.log(`  ✗ No array match for:`, cellValue.map(c => {
                    //         if (typeof c === 'object') {
                    //             return c.title || c.degree || c.approval_date || c.name || 'unknown';
                    //         }
                    //         return c;
                    //     }));
                    // }
                    return matches;
                }
                
                // Handle comma-separated strings (like "AL, AZ, AR")
                if (typeof cellValue === 'string' && cellValue.includes(',')) {
                    const values = cellValue.split(',').map(v => v.trim());
                    const matches = values.some(v => allowedValues.includes(v));
                    // if (matches) { // Debug: Log comma-separated match
                    //     console.log(`  ✓ Comma-separated match in: "${cellValue}"`);
                    // } else {
                    //     console.log(`  ✗ No match in comma-separated: "${cellValue}"`, 'Split values:', values);
                    // }
                    return matches;
                }
                
                // Handle single values
                const match = allowedValues.includes(String(cellValue));
                // if (match) { // Debug: Log single value match
                //     console.log(`  ✓ Single value match: "${cellValue}"`);
                // } else {
                //     console.log(`  ✗ No single value match: "${cellValue}"`);
                // }
                return match;
            });
            
            // console.log(`After filtering "${columnKey}": ${beforeCount} → ${filteredData.length} rows`); // Debug: Log filter result
        });
        
        // console.log('\n✅ Final filtered count:', filteredData.length); // Debug: Log final count
        
        this.filteredData = filteredData;
        this.currentPage = 1; // Reset to first page
        this.render();
    }


    attachFilterEvents() {
        const filterBtns = this.thead.querySelectorAll('.filter-btn');
        filterBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column')
                this.toggleFilterOptions(column);
            })
        })

        const filterApplyBtns = this.thead.querySelectorAll('.filter-apply-btn');
        filterApplyBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column')
                this.applyFilter(column);
            })
        })

        const filterClearBtns = this.thead.querySelectorAll('.filter-clear-btn');
        filterClearBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column')
                this.clearFilter(column);
            })
        })
    }

    updateHeaderDisplay() {
        const visibleColumns = this.columnKeys.filter(key => !this.hiddenColumns.has(key));
        
        const visibleHeaders = visibleColumns.map((key) => {
            // Find the original index to get the correct header
            const originalIndex = this.columnKeys.indexOf(key);

            // Use the provided header names from this.headers
            let displayName;
            if (this.headers && this.headers[originalIndex]) {
                displayName = this.headers[originalIndex];
            } else {
                // Fallback to formatted field name if header not available
                displayName = this.formatHeaderName(key);
            }
            return `<th role="columnheader" data-column="${key}" scope="col" aria-sort="${this.currentSortDirection}" aria-label="${displayName}">
            <div class="data-table-header_contents">
            <div class="data-table-header_contents-left">
            <span>${displayName}</span>
            ${this.enableValueFilters ? `<div class="filter-btn-wrapper">
                <button class="filter-btn" data-table-id="${this.dataset}" data-column="${key}" aria-label="Filter by ${displayName}">
                    <img src="${this.filterIconSrc}" alt="Filter" role="presentation" />
                </button>
                <div id="filter-options-${this.dataset}-${key}" class="filter-options hidden">
                    <div class="filter-actions">
                        <button class="filter-apply-btn" data-column="${key}">Apply</button>
                        <button class="filter-clear-btn" data-column="${key}">Clear</button>
                    </div>
                    <div class="filter-checkbox-list">
                        ${this.getUniqueColumnValues(key).map(value => {
                            const isChecked = this.activeFilters[key]?.includes(value) ? 'checked' : '';
                            return `
                            <div class="filter-checkbox-item">
                                <label>
                                    <input type="checkbox" value="${value}" data-column="${key}" class="filter-checkbox" ${isChecked} />
                                    <span>${value}</span>
                                </label>
                            </div>
                        `;
                        }).join('')}
                    </div>
                </div>
            </div>
            <button class="sort-btn" data-column="${key}" data-direction="${this.currentSortDirection}" aria-label="Sort by ${displayName}">
                <img src="${this.sortIconSrc}" alt="Sort" role="presentation" />
            </button>
            </div>` : ''}
            </th>`;
        }).join('');
        
        this.thead.innerHTML = visibleHeaders;
        const sortBtns = this.thead.querySelectorAll('.sort-btn');
        this.attachSortEvents(sortBtns);
        this.attachFilterEvents();
    }

    attachSortEvents(sortBtns) {
        sortBtns.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const column = e.currentTarget.getAttribute('data-column');
                // Determine new sort direction based on current state
                let direction;
                if (this.currentSortColumn === column) {
                    direction = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
                } else {
                    direction = 'asc';
                }
                this.sortByColumn(column, direction, btn);
            });
        });
    }

    sortByColumn(column, direction, btn) {
        /*console.log(`📊 Sorting by column: "${column}" in ${direction} order`, {
            isAutoSort: btn === null,
            hasButton: !!btn,
            previousColumn: this.currentSortColumn,
            previousDirection: this.currentSortDirection
        });*/
        
        // Update current sort state
        this.currentSortColumn = column;
        this.currentSortDirection = direction;
        
        const sortedData = this.filteredData.sort((a, b) => {
            return this.sortValues(a[column], b[column], direction);
        });
        
        /*console.log(`✓ Sort completed - First 3 items:`, sortedData.slice(0, 3).map(item => ({
            [column]: item[column]
        })));*/
        
        this.filteredData = sortedData;
        this.render();
        this.toggleSortDirection(btn);
    }

    toggleSortDirection(btn) {
        if (!btn) {
            /*console.log('⚠️ No button to toggle (auto-sort or programmatic sort)');*/
            return;
        }
        const direction = btn.getAttribute('data-direction');
        const newDirection = direction === 'desc' ? 'asc' : 'desc';
        btn.setAttribute('data-direction', newDirection);
        // console.log(`🔄 Button direction toggled: ${direction} → ${newDirection}`);
    }

    sortValues(valA, valB, direction) {
        
        // Handle different data types
        if (typeof valA === 'number' && typeof valB === 'number') {
            return direction === 'asc' ? valA - valB : valB - valA;
        }
        
        // Handle dates
        if (this.isDate(valA) && this.isDate(valB)) {
            return direction === 'asc' 
                ? new Date(valA) - new Date(valB)
                : new Date(valB) - new Date(valA);
        }
        
        // Default to string comparison
        return direction === 'asc' 
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
    }

    isDate(value) {
        return value instanceof Date || !isNaN(Date.parse(value));
    }

    createImage(src, alt) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = alt;
        img.className = 'cell-image';
        return img;
    }

    createLink(url, text) {
        const link = document.createElement('a');
        link.href = url;
        link.textContent = text;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        return link;
    }

    createLinkedList(items, textKey, urlKey) {
        const list = document.createElement('ul');
        list.classList.add('data-table-list');
        
        items.forEach(item => {
            if (item[textKey] && item[textKey].trim()) { // Only if text exists
                const listItem = document.createElement('li');
                
                if (item[urlKey] && item[urlKey].trim()) {
                    const link = this.createLink(item[urlKey], item[textKey]);
                    listItem.appendChild(link);
                } else {
                    listItem.textContent = item[textKey];
                }
                
                list.appendChild(listItem);
            }
        });
        
        return list;
    }

    validateDataItem(item) {
        return {
            isValid: typeof item === 'object' && item !== null,
            hasUrl: typeof item.url === 'string',
            hasImage: typeof item.image === 'string',
            hasCourses: Array.isArray(item.courses),
            hasDegrees: Array.isArray(item.degrees),
            hasApprovalDates: Array.isArray(item.approval_dates),
        };
    }

    // Accessibility
    initializeAccessibility() {
        this.tableElement.setAttribute('role', 'table');
        this.tableElement.setAttribute('aria-rowcount', this.processedData.length);
        this.tableElement.setAttribute('aria-colcount', this.columnKeys);

        this.setupKeyboardNavigation()
    }

    setupKeyboardNavigation() {
        // Make sortable headers keyboard accessible
        this.tableElement.addEventListener('keydown', (e) => {
            if (e.target.matches('.sort-btn')) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.target.click();
                }
            }
        });
        
        // Manage focus in dropdowns
        this.setupDropdownKeyboardNavigation();
    }

    setupDropdownKeyboardNavigation() {
        if (!this.hasToolbar || !this.dropdownMenu || !this.dropdownToggle) return;
        
        this.dropdownToggle.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                e.preventDefault();
                this.openDropdown();
                this.focusFirstMenuItem();
            }
        });
        
        this.dropdownMenu.addEventListener('keydown', (e) => {
            const menuItems = this.dropdownMenu.querySelectorAll('.dropdown-item input[type="checkbox"]');
            const currentIndex = Array.from(menuItems).indexOf(e.target);
            
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    const nextIndex = currentIndex === menuItems.length - 1 ? 0 : currentIndex + 1;
                    menuItems[nextIndex].focus();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    const prevIndex = currentIndex === 0 ? menuItems.length - 1 : currentIndex - 1;
                    menuItems[prevIndex].focus();
                    break;
                case 'Enter':
                    e.preventDefault();
                    const checkbox = e.target;
                    checkbox.checked = !checkbox.checked;
                    checkbox.dispatchEvent(new Event('change'));
                    break;
                case 'Escape':
                    e.preventDefault();
                    this.closeDropdown();
                    this.dropdownToggle.focus();
                    break;
            }
        });
    }

    openDropdown() {
        if (!this.hasToolbar || !this.dropdownMenu || !this.dropdownToggle) return;
        
        this.dropdownMenu.classList.add('show');
        this.dropdownToggle.setAttribute('aria-expanded', 'true');
        this.dropdownToggle.focus();
    }

    closeDropdown() {
        if (!this.hasToolbar || !this.dropdownMenu || !this.dropdownToggle) return;
        
        this.dropdownMenu.classList.remove('show');
        this.dropdownToggle.setAttribute('aria-expanded', 'false');
    }

    focusFirstMenuItem() {
        if (!this.hasToolbar || !this.dropdownMenu) return;
        
        const firstItem = this.dropdownMenu.querySelector('.dropdown-item');
        if(firstItem) {
            firstItem.querySelector('input[type="checkbox"]').focus();
        }
    }
}  